<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import WootModal from 'dashboard/components/Modal.vue';

const props = defineProps({
  visible: { type: Boolean, required: true },
  field: { type: Object, default: null },
});
const emit = defineEmits(['close', 'saved']);
const store = useStore();
const { t } = useI18n();

const isEdit = computed(() => Boolean(props.field?.id));
const form = ref({
  label: '',
  key: '',
  field_type: 'single',
  placeholder: '',
  required: false,
  include_in_google_description: true,
});
const error = ref(null);

watch(
  () => props.visible,
  v => {
    if (v) {
      form.value = props.field
        ? { ...props.field }
        : {
            label: '',
            key: '',
            field_type: 'single',
            placeholder: '',
            required: false,
            include_in_google_description: true,
          };
      error.value = null;
    }
  }
);

const slugify = value =>
  value
    .toString()
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

watch(
  () => form.value.label,
  v => {
    if (!isEdit.value) form.value.key = slugify(v);
  }
);

const submit = async () => {
  error.value = null;
  try {
    const payload = { ...form.value };
    if (isEdit.value) {
      delete payload.key;
      await store.dispatch('painelFieldDefinitions/update', {
        id: props.field.id,
        payload,
      });
    } else {
      await store.dispatch('painelFieldDefinitions/create', payload);
    }
    emit('saved');
  } catch (err) {
    if (err?.response?.status === 422) {
      error.value = t('PAINEL.FIELD_DEFINITIONS.DUPLICATE_KEY');
    } else {
      throw err;
    }
  }
};
</script>

<template>
  <WootModal :show="visible" :on-close="() => emit('close')" show-close-button>
    <div class="p-6">
      <h3 class="mb-4 text-lg font-semibold">
        {{
          isEdit
            ? t('PAINEL.FIELD_DEFINITIONS.FORM_TITLE_EDIT')
            : t('PAINEL.FIELD_DEFINITIONS.FORM_TITLE_NEW')
        }}
      </h3>

      <form class="space-y-3" @submit.prevent="submit">
        <label class="block">
          <span class="text-xs uppercase text-n-slate-11">{{
            t('PAINEL.FIELD_DEFINITIONS.FORM_LABEL_NAME')
          }}</span>
          <input
            v-model="form.label"
            type="text"
            required
            class="mt-1 w-full rounded border border-n-strong px-2 py-1"
          />
        </label>

        <label class="block">
          <span class="text-xs uppercase text-n-slate-11">{{
            t('PAINEL.FIELD_DEFINITIONS.FORM_LABEL_KEY')
          }}</span>
          <input
            v-model="form.key"
            type="text"
            :readonly="isEdit"
            class="mt-1 w-full rounded border border-n-strong bg-n-surface-1 px-2 py-1 font-mono"
          />
          <span class="text-xs text-n-slate-11">{{
            t('PAINEL.FIELD_DEFINITIONS.FORM_LABEL_KEY_HELP')
          }}</span>
        </label>

        <label class="block">
          <span class="text-xs uppercase text-n-slate-11">{{
            t('PAINEL.FIELD_DEFINITIONS.FORM_LABEL_TYPE')
          }}</span>
          <select
            v-model="form.field_type"
            class="mt-1 w-full rounded border border-n-strong px-2 py-1"
          >
            <option value="single">
              {{ t('PAINEL.FIELD_DEFINITIONS.TYPE_SINGLE') }}
            </option>
            <option value="multi">
              {{ t('PAINEL.FIELD_DEFINITIONS.TYPE_MULTI') }}
            </option>
          </select>
        </label>

        <label class="block">
          <span class="text-xs uppercase text-n-slate-11">{{
            t('PAINEL.FIELD_DEFINITIONS.FORM_LABEL_PLACEHOLDER')
          }}</span>
          <input
            v-model="form.placeholder"
            type="text"
            class="mt-1 w-full rounded border border-n-strong px-2 py-1"
          />
        </label>

        <label class="flex items-center gap-2">
          <input v-model="form.required" type="checkbox" />
          <span class="text-sm">{{
            t('PAINEL.FIELD_DEFINITIONS.FORM_TOGGLE_REQUIRED')
          }}</span>
        </label>

        <label class="flex items-center gap-2">
          <input v-model="form.include_in_google_description" type="checkbox" />
          <span class="text-sm">{{
            t('PAINEL.FIELD_DEFINITIONS.FORM_TOGGLE_GOOGLE')
          }}</span>
        </label>

        <div
          v-if="error"
          class="rounded bg-n-ruby-3 p-2 text-sm text-n-ruby-12"
        >
          {{ error }}
        </div>

        <div class="flex justify-end gap-2 pt-2">
          <button
            type="button"
            class="rounded bg-n-slate-3 px-4 py-2 text-sm"
            @click="emit('close')"
          >
            {{ t('PAINEL.FIELD_DEFINITIONS.FORM_CANCEL') }}
          </button>
          <button
            type="submit"
            class="rounded bg-n-brand px-4 py-2 text-sm font-semibold text-white"
          >
            {{ t('PAINEL.FIELD_DEFINITIONS.FORM_SAVE') }}
          </button>
        </div>
      </form>
    </div>
  </WootModal>
</template>
